import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, UserCheck, UserX } from 'lucide-react';
import type { PersonDTO } from '../global';
import { displaySpeaker } from '../features/recording/speakerLabels';
import { t } from '../lib/i18n';

interface SpeakerAssignMenuProps {
  speakerLabel: string;
  currentPersonId?: string;
  isDarkMode: boolean;
  onAssign: (personId: string | null) => Promise<void>;
  onClose: () => void;
}

/**
 * Transkriptteki bir konuşmacı etiketini ("Konuşmacı 1") Kişiler
 * veritabanındaki birine bağlayan açılır menü. Eşleme sonrası transkriptte
 * kişinin adı görünür, kişi profilinde bu toplantı ve söyledikleri listelenir.
 */
export const SpeakerAssignMenu: React.FC<SpeakerAssignMenuProps> = ({
  speakerLabel,
  currentPersonId,
  isDarkMode,
  onAssign,
  onClose,
}) => {
  const [people, setPeople] = useState<PersonDTO[] | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void window.zabit.listPeople().then(setPeople).catch((err) => setError((err as Error).message));
  }, []);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr');
    const list = people ?? [];
    return (q ? list.filter((p) => [p.name, p.email, p.companyName].some((v) => v?.toLocaleLowerCase('tr').includes(q))) : list).slice(0, 30);
  }, [people, query]);

  const choose = async (personId: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await onAssign(personId);
      onClose();
    } catch (err) {
      setError((err as Error).message || t('Eşleme kaydedilemedi.', 'Could not save the match.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={ref}
      className={`absolute z-30 mt-1 w-64 rounded-xl border shadow-xl p-2 space-y-1.5 ${
        isDarkMode ? 'bg-[#19191C] border-[#3F3F45]' : 'bg-white border-neutral-200'
      }`}
    >
      <div className="text-[11px] text-neutral-500 px-1.5">
        {t(`“${displaySpeaker(speakerLabel)}” kim?`, `Who is “${displaySpeaker(speakerLabel)}”?`)}
      </div>
      <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-black/10">
        <Search className="w-3.5 h-3.5 text-neutral-500" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('Kişi ara…', 'Search people…')}
          className="bg-transparent outline-none text-[12.5px] w-full"
        />
      </div>
      <div className="max-h-56 overflow-y-auto no-scrollbar">
        {!people && !error && (
          <p className="text-[12px] text-neutral-500 px-2 py-2 flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('Kişiler yükleniyor…', 'Loading people…')}
          </p>
        )}
        {people && filtered.length === 0 && (
          <p className="text-[12px] text-neutral-500 px-2 py-2">
            {t('Kişi bulunamadı. Kişiler ekranından ekleyebilirsin.', 'No people found. You can add them from the People screen.')}
          </p>
        )}
        {filtered.map((p) => (
          <button
            key={p.id}
            disabled={busy}
            onClick={() => void choose(p.id)}
            className={`w-full text-left px-2 py-1.5 rounded-lg text-[12.5px] flex items-center justify-between gap-2 hover:bg-[#3AC8E0]/10 ${
              p.id === currentPersonId ? 'text-[#46CBE2] font-semibold' : 'text-neutral-300'
            }`}
          >
            <span className="truncate">
              {p.name}
              {p.companyName && <span className="text-neutral-500 font-normal"> · {p.companyName}</span>}
            </span>
            {p.id === currentPersonId && <UserCheck className="w-3.5 h-3.5 shrink-0" />}
          </button>
        ))}
      </div>
      {currentPersonId && (
        <button
          disabled={busy}
          onClick={() => void choose(null)}
          className="w-full text-left px-2 py-1.5 rounded-lg text-[12px] text-rose-400 hover:bg-rose-500/10 flex items-center gap-1.5"
        >
          <UserX className="w-3.5 h-3.5" /> {t('Eşlemeyi kaldır', 'Remove match')}
        </button>
      )}
      {error && <p className="text-[11.5px] text-rose-400 px-1.5">{error}</p>}
    </div>
  );
};
